/**
 * Expandable Tile Component
 * Stat tile that expands inline to show detail panel
 */

import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { ExpandedContent, TileHeader } from './expandable-tile/expandable-tile.parts'

interface ExpandableTileProps {
  /** Tile title */
  title: string
  /** Main value to display */
  value: string | number
  /** Icon component */
  icon: React.ElementType
  /** Link for "View All" action (optional - if not provided, no link is shown) */
  viewAllLink?: string
  /** Label for "View All" link */
  viewAllLabel?: string
  /** Content to show when expanded */
  children?: ReactNode
  /** Loading state for expanded content */
  loading?: boolean
  /** Callback when tile is expanded */
  onExpand?: () => void
}

export function ExpandableTile({
  title,
  value,
  icon: Icon,
  viewAllLink,
  viewAllLabel,
  children,
  loading = false,
  onExpand,
}: ExpandableTileProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false)
  const { t } = useTranslation('admin')

  const handleToggle = (): void => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)
    if (newExpanded && onExpand) {
      onExpand()
    }
  }

  return (
    <div
      className="rounded-xl border transition-shadow hover:shadow-md"
      style={{ borderColor: 'var(--card-border)', background: 'var(--bg-card)' }}
    >
      <TileHeader
        title={title}
        value={value}
        Icon={Icon}
        isExpanded={isExpanded}
        onToggle={handleToggle}
      />
      <ExpandedContent
        isExpanded={isExpanded}
        loading={loading}
        viewAllLink={viewAllLink}
        viewAllLabel={viewAllLabel ?? t('dashboard.viewAll')}
      >
        {children}
      </ExpandedContent>
    </div>
  )
}

/**
 * Simple stat row for expanded tile content
 */
interface StatRowProps {
  label: string
  value: string | number
  highlight?: boolean
}

export function StatRow({ label, value, highlight = false }: StatRowProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <span
        className="text-sm font-medium"
        style={{ color: highlight ? 'var(--accent)' : 'var(--text)' }}
      >
        {value}
      </span>
    </div>
  )
}

/**
 * Mini table for expanded tile content
 */
interface MiniTableProps {
  headers: string[]
  rows: Array<{ cells: ReactNode[]; key: string }>
}

export function MiniTable({ headers, rows }: MiniTableProps): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: 'var(--table-header)' }}>
            {headers.map((header, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left text-xs font-medium tracking-wider uppercase"
                style={{ color: 'var(--text-muted)' }}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-t" style={{ borderColor: 'var(--border)' }}>
              {row.cells.map((cell, i) => (
                <td key={i} className="px-3 py-2" style={{ color: 'var(--text-dim)' }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
